"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateTimeField = void 0;
const joi_1 = __importDefault(require("joi"));
const datasheet_1 = require("../datasheet");
const date_time_base_field_1 = require("./date_time_base_field");
const field_types_1 = require("../../types/field_types");
const validate_schema_1 = require("./validate_schema");
const dayjs_1 = __importDefault(require("dayjs"));
class DateTimeField extends date_time_base_field_1.DateTimeBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.DateTime,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return {
            dateFormat: field_types_1.DateFormat['YYYY/MM/DD'],
            timeFormat: field_types_1.TimeFormat['hh:mm'],
            includeTime: false,
            autoFill: false,
        };
    }
    defaultValue() {
        if (this.field.property.autoFill) {
            return Date.now();
        }
        return null;
    }
    /* Due to the need to traverse the DateTimeFormat enumeration value,
    but DateTimeFormat will have the form of keyValue and valueKey after compilation
      Need to filter out the case of number key */
    validateProperty() {
        return DateTimeField.propertySchema.validate(this.field.property);
    }
    validateCellValue(cv) {
        return DateTimeField.cellValueSchema.validate(cv);
    }
    validateOpenWriteValue(owv) {
        return DateTimeField.openWriteValueSchema.validate(owv);
    }
    get includeTimeZone() {
        var _a;
        return (_a = this.field.property) === null || _a === void 0 ? void 0 : _a.includeTimeZone;
    }
    get timeZone() {
        return this.field.property.timeZone;
    }
    get openFieldProperty() {
        const { autoFill, includeTime, dateFormat, timeFormat } = this.field.property;
        return {
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: field_types_1.TimeFormat[timeFormat],
            autoFill,
            includeTime
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return DateTimeField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { dateFormat, timeFormat, autoFill, includeTime } = openFieldProperty;
        const defaultProperty = DateTimeField.defaultProperty();
        return {
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: timeFormat ? field_types_1.TimeFormat[timeFormat] : defaultProperty.timeFormat,
            autoFill: autoFill !== null && autoFill !== void 0 ? autoFill : defaultProperty.autoFill,
            includeTime: includeTime !== null && includeTime !== void 0 ? includeTime : defaultProperty.includeTime
        };
    }
}
exports.DateTimeField = DateTimeField;
DateTimeField.propertySchema = joi_1.default.object({
    dateFormat: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.TimeFormat)).required(),
    includeTime: joi_1.default.boolean().required(),
    autoFill: joi_1.default.boolean().required(),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
DateTimeField.cellValueSchema = joi_1.default.number().custom((value, helpers) => {
    if ((0, dayjs_1.default)(value).isValid()) {
        return value;
    }
    return helpers.error('valid date');
}, 'custom validation').allow(null).required();
DateTimeField.openWriteValueSchema = joi_1.default.custom((value, helpers) => {
    if ((0, dayjs_1.default)(value).isValid()) {
        return value;
    }
    return helpers.error('valid date');
}).allow(null).required();
DateTimeField.defaultDateFormat = field_types_1.DateFormat[0];
DateTimeField.defaultTimeFormat = field_types_1.TimeFormat[0];
DateTimeField.openUpdatePropertySchema = joi_1.default.object({
    dateFormat: joi_1.default.valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.TimeFormat)),
    includeTime: joi_1.default.boolean(),
    autoFill: joi_1.default.boolean(),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
//# sourceMappingURL=date_time_field.js.map